import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from 'react';

import { Stack, Typography } from '@mui/material';
import {
  DataGridPremium,
  DataGridPremiumProps,
  GRID_REORDER_COL_DEF,
  GRID_TREE_DATA_GROUPING_FIELD,
  gridFilteredDescendantCountLookupSelector,
  GridGroupNode,
  GridRenderCellParams,
  GridRowId,
  GridRowOrderChangeParams,
  gridRowTreeSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import styles from './PlanningTable.module.scss';
import { flattenTasks } from '../../helpers';
import { useColumns } from '../../hooks/useColumns';
import { TaskRow, PlanningGridProps } from '../../types';
import { useMoveTask } from '@/api/REST/projects';
import {
  DATAGRID_CUSTOM_CLASS_NAMES,
  dataGridCellFontFamilyTabularNumsClassName,
} from '@/assets/themes/components/MuiDataGrid';
import { Icon } from '@/components/ui/Icon';
import { IconButton } from '@/components/ui/IconButton';
import { NoRowsState } from '@/components/ui/NoRowsState';
import { uiIcons } from '@/constants/uiIcons';

const MAX_WBS_DEPTH = 4; // 0-indexed; enforces a max of 5 WBS levels

const getSubtreeMaxDepth = (
  nodeId: GridRowId,
  rowTree: ReturnType<typeof gridRowTreeSelector>,
): number => {
  const node = rowTree[nodeId];
  if (!node || node.type !== 'group') return node?.depth ?? 0;
  const groupNode = node as GridGroupNode;
  if (!groupNode.children?.length) return node.depth;
  return Math.max(
    ...groupNode.children.map((childId) =>
      getSubtreeMaxDepth(childId, rowTree),
    ),
  );
};

export const PlanningTable = ({
  projectId,
  tasks,
  currency,
  isLoading,
}: PlanningGridProps) => {
  const apiRef = useGridApiRef();
  const rows = flattenTasks(tasks);
  const { mutate: moveTask } = useMoveTask();
  const columns = useColumns({ currency });
  const { t } = useTranslation('ui');

  const isDragActiveRef = useRef(false);
  const invalidTargetRef = useRef<{
    nodeId: GridRowId | null;
    reason: string | null;
  }>({ nodeId: null, reason: null });
  const [tooltipState, setTooltipState] = useState<{
    reason: string;
    left: number;
    top: number;
  } | null>(null);

  useEffect(() => {
    const onDragStart = () => {
      isDragActiveRef.current = true;
    };
    const clear = () => {
      isDragActiveRef.current = false;
      invalidTargetRef.current = { nodeId: null, reason: null };
      setTooltipState(null);
    };
    // Capture phase so we intercept dragend before MUI can stop propagation
    document.addEventListener('dragstart', onDragStart, true);
    document.addEventListener('dragend', clear, true);
    // pointerup as fallback in case MUI uses pointer capture instead of HTML5 DnD
    document.addEventListener('pointerup', clear);
    return () => {
      document.removeEventListener('dragstart', onDragStart, true);
      document.removeEventListener('dragend', clear, true);
      document.removeEventListener('pointerup', clear);
    };
  }, []);

  const setTreeDataPath = useCallback(
    (path: string[], row: TaskRow): TaskRow => {
      return { ...row, path };
    },
    [],
  );

  const handleRowOrderChange = useCallback(
    (params: GridRowOrderChangeParams) => {
      const movedRow = params.row as TaskRow;
      if (!movedRow?.id) return;

      const newPath = [...movedRow.path];
      const taskId = movedRow.id;
      const parentPath = newPath.slice(0, -1);
      const parentKey = parentPath.join('/');
      const currentRows = (apiRef.current?.getSortedRows() ?? []) as TaskRow[];

      const newParentId =
        parentPath.length === 0
          ? null
          : (currentRows.find((r) => r.path.join('/') === parentKey)?.id ??
            null);

      // targetIndex is the sibling-group index (position among the new parent's
      // children), not a global flat display index. Filter to just the siblings
      // and index directly into that list.
      const siblings = currentRows.filter(
        (r) =>
          r.id !== taskId &&
          r.path.length === newPath.length &&
          r.path.slice(0, -1).join('/') === parentKey,
      );

      const afterTaskId =
        params.targetIndex > 0
          ? (siblings[params.targetIndex - 1]?.id ?? null)
          : null;

      moveTask({ projectId, taskId, payload: { newParentId, afterTaskId } });
    },
    [apiRef, moveTask, projectId],
  );

  const isValidRowReorder = useCallback<
    NonNullable<DataGridPremiumProps['isValidRowReorder']>
  >(
    (context) => {
      const { sourceNode, targetNode, dropPosition } = context;
      let reason: string | null = null;

      if (dropPosition === 'inside') {
        const targetRow = apiRef.current?.getRow(
          targetNode.id,
        ) as TaskRow | null;
        if (targetRow?.hasAllocations) {
          reason = t('PlanningDragInvalidHasAllocations');
        }
      }

      if (!reason) {
        const rowTree = gridRowTreeSelector(apiRef);
        const subtreeHeight =
          getSubtreeMaxDepth(sourceNode.id, rowTree) - sourceNode.depth;
        const newSourceDepth =
          dropPosition === 'inside' ? targetNode.depth + 1 : targetNode.depth;
        if (newSourceDepth + subtreeHeight > MAX_WBS_DEPTH) {
          reason = t('PlanningDragInvalidMaxDepth');
        }
      }

      const newNodeId = reason !== null ? targetNode.id : null;
      const prev = invalidTargetRef.current;
      if (
        isDragActiveRef.current &&
        (reason !== prev.reason || newNodeId !== prev.nodeId)
      ) {
        invalidTargetRef.current = { nodeId: newNodeId, reason };
        if (reason !== null) {
          const rowEl = document.querySelector<HTMLElement>(
            `[data-id="${targetNode.id}"]`,
          );
          if (rowEl) {
            const rect = rowEl.getBoundingClientRect();
            setTooltipState({
              reason,
              left: rect.left,
              top: rect.top,
            });
          }
        } else {
          setTooltipState(null);
        }
      }

      return reason === null;
    },
    [apiRef, t],
  );

  const CustomGroupingHeader = () => {
    const apiRef = useGridApiContext();
    const isAnyExpanded = useGridSelector(apiRef, (state) =>
      Object.values(gridRowTreeSelector(state)).some(
        (node) => node.type === 'group' && node.childrenExpanded,
      ),
    );

    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <IconButton
          iconName={isAnyExpanded ? 'unfold_less' : 'unfold_more'}
          tooltip={
            isAnyExpanded
              ? t('CollapseAllButtonText')
              : t('ExpandAllButtonText')
          }
          size="tiny"
          onClick={() =>
            isAnyExpanded
              ? apiRef.current.collapseAllRows()
              : apiRef.current.expandAllRows()
          }
          tabIndex={-1}
          data-automation-id="planning-table-collapse-expand-all-button"
        />
        <Typography variant="label" color="text.secondary">
          {t('general.Name')}
        </Typography>
      </Stack>
    );
  };

  const CustomGridTreeDataGroupingCell = (props: GridRenderCellParams) => {
    const { id, field, row, rowNode } = props;
    const apiRef = useGridApiContext();
    const filteredDescendantCountLookup = useGridSelector(
      apiRef,
      gridFilteredDescendantCountLookupSelector,
    );
    const filteredDescendantCount =
      filteredDescendantCountLookup[rowNode.id] ?? 0;
    const isGroup = rowNode.type === 'group';
    const isExpanded = isGroup && rowNode.childrenExpanded;

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      if (!isGroup) return;
      apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
      apiRef.current.setCellFocus(id, field);
      event.stopPropagation();
    };

    return (
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          // Allow sx prop as we need to calculate the left margin based on the depth of the node in the tree
          ml: rowNode.depth * 4,
        }}
      >
        <IconButton
          iconName={isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
          tooltip={isExpanded ? 'Collapse' : 'Expand'}
          size="tiny"
          onClick={handleClick}
          tabIndex={-1}
          disabled={!isGroup || filteredDescendantCount === 0}
          disableTooltip={!isGroup || filteredDescendantCount === 0}
          data-automation-id={`planning-table-expand-collapse-button-${id}`}
        />
        <span>{row.name}</span>
      </Stack>
    );
  };

  const groupingColDef: DataGridPremiumProps['groupingColDef'] = {
    headerName: 'Name',
    cellClassName: dataGridCellFontFamilyTabularNumsClassName,
    width: 300,
    renderHeader: () => <CustomGroupingHeader />,
    renderCell: (params) => <CustomGridTreeDataGroupingCell {...params} />,
  };

  return (
    <>
      <DataGridPremium
        apiRef={apiRef}
        treeData
        rows={rows}
        columns={columns}
        getRowClassName={(params) =>
          (params.row as TaskRow).path.length > 1
            ? DATAGRID_CUSTOM_CLASS_NAMES.ROW_TREE_LEAF
            : ''
        }
        getTreeDataPath={(row) => row.path}
        setTreeDataPath={setTreeDataPath}
        defaultGroupingExpansionDepth={-1}
        disableRowSelectionOnClick
        hideFooter
        rowSelectionPropagation={{ descendants: false, parents: false }}
        rowReordering
        isValidRowReorder={isValidRowReorder}
        onRowOrderChange={handleRowOrderChange}
        groupingColDef={groupingColDef}
        disableColumnMenu
        disableColumnSorting
        pinnedColumns={{
          left: [GRID_REORDER_COL_DEF.field, GRID_TREE_DATA_GROUPING_FIELD],
        }}
        pinnedColumnsSectionSeparator="shadow"
        slots={{
          noRowsOverlay: () => <NoRowsState />,
        }}
        loading={isLoading}
      />
      {tooltipState !== null &&
        createPortal(
          <Stack
            direction="row"
            alignItems="center"
            className={styles.DragTooltip}
            style={{ left: tooltipState.left, top: tooltipState.top }}
          >
            <Icon name={uiIcons.status.errorOutline} color="error" />
            <span>{tooltipState.reason}</span>
          </Stack>,
          document.body,
        )}
    </>
  );
};
